import { Box, chakra, Flex, Image, Text, Textarea } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import React from "react";
import { LuArrowUp, LuChevronLeft, LuInfo } from "react-icons/lu";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import type { Companion } from "src/models/companion";
import { CompanionAvatar } from "src/components/CompanionAvatar";
import { CompanionSheet } from "src/components/CompanionSheet";
import { useChatStore } from "src/stores/chatStoreContext";
import type { ChatMessage } from "src/stores/ChatStore";
import { Loading } from "components/Loading";
import { ACCENT_GRADIENT, withAlpha } from "src/theme";
import { tap } from "src/utils/haptic";

/** Tap-to-send openers, shown above the input while it's empty. */
const QUICK_REPLIES = [
  "How's your day going?",
  "I had a long day",
  "I need some advice",
  "Tell me something fun",
  "I'm a bit stressed",
  "What are you up to?",
];

function IntroCard({ companion }: { companion: Companion }) {
  return (
    <Box
      mx="auto"
      mb={4}
      width="100%"
      maxW="320px"
      borderRadius="24px"
      overflow="hidden"
      bg="surface"
      borderWidth="1px"
      borderColor={withAlpha(companion.color, 0.35)}
      animation="tlRise 0.4s ease both"
    >
      {companion.avatarURL && (
        <Image
          src={companion.avatarURL}
          alt=""
          width="100%"
          aspectRatio="16 / 10"
          objectFit="cover"
          draggable={false}
        />
      )}
      <Box p={4} textAlign="center">
        <Text fontWeight="bold">{companion.name}</Text>
        <Text fontSize="sm" color="muted" mt={1}>
          {companion.tagline}
        </Text>
      </Box>
    </Box>
  );
}

function Bubble({
  message,
  companion,
  first,
  last,
}: {
  message: ChatMessage;
  companion: Companion;
  /** First / last of a run of messages from the same side. */
  first: boolean;
  last: boolean;
}) {
  const mine = message.from === "user";
  const big = "22px";
  const small = "6px";
  return (
    <Flex
      justify={mine ? "flex-end" : "flex-start"}
      align="flex-end"
      gap={2}
      mt={first ? 3 : 0.5}
    >
      {!mine && (
        <Box w="28px" flexShrink={0}>
          {last && <CompanionAvatar companion={companion} size="28px" />}
        </Box>
      )}
      <Box
        maxW="76%"
        px={4}
        py="10px"
        fontSize="15px"
        lineHeight="1.4"
        whiteSpace="pre-wrap"
        wordBreak="break-word"
        color="white"
        background={mine ? ACCENT_GRADIENT : "elevated"}
        borderTopLeftRadius={!mine && !first ? small : big}
        borderBottomLeftRadius={!mine && !last ? small : big}
        borderTopRightRadius={mine && !first ? small : big}
        borderBottomRightRadius={mine && !last ? small : big}
        animation="tlRise 0.25s ease both"
        opacity={message.pending ? 0.7 : 1}
      >
        {message.text}
      </Box>
    </Flex>
  );
}

/** Under a user message that failed or was blocked. */
function BubbleNote({
  message,
  onRetry,
}: {
  message: ChatMessage;
  onRetry: (m: ChatMessage) => void;
}) {
  if (message.failed) {
    return (
      <Flex justify="flex-end" mt={1}>
        <chakra.button
          type="button"
          fontSize="xs"
          color="red.300"
          onClick={() => onRetry(message)}
        >
          Not sent · Tap to retry
        </chakra.button>
      </Flex>
    );
  }
  if (message.blocked) {
    return (
      <Text textAlign="right" fontSize="xs" color="faint" mt={1}>
        Couldn't be answered. Let's keep things friendly.
      </Text>
    );
  }
  return null;
}

function TypingBubble({ companion }: { companion: Companion }) {
  return (
    <Flex align="flex-end" gap={2} mt={3} animation="tlFade 0.2s ease both">
      <CompanionAvatar companion={companion} size="28px" />
      <Flex
        bg="elevated"
        borderRadius="22px"
        px={4}
        h="40px"
        align="center"
        gap="5px"
        aria-label="Typing"
      >
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            boxSize="7px"
            borderRadius="full"
            bg="white"
            animation="tlDot 1.2s ease-in-out infinite"
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
      </Flex>
    </Flex>
  );
}

const Chat = observer(({ companion }: { companion: Companion }) => {
  const chatStore = useChatStore();
  const navigate = useNavigate();
  const [draft, setDraft] = React.useState("");
  const [showProfile, setShowProfile] = React.useState(false);
  const endRef = React.useRef<HTMLDivElement>(null);
  const thread = chatStore.thread(companion.id);
  const typing = chatStore.isTyping(companion.id);

  const [openError, setOpenError] = React.useState(false);
  const open = React.useCallback(() => {
    setOpenError(false);
    chatStore.openChat(companion).catch((err) => {
      console.error("Failed to open chat:", err);
      setOpenError(true);
    });
  }, [chatStore, companion]);

  React.useEffect(open, [open]);

  const retry = (m: ChatMessage) => void chatStore.retry(m);

  React.useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [thread.length, typing]);

  const ready = chatStore.hasChat(companion.id);

  function send(text: string) {
    if (!text.trim() || typing || !ready) return;
    tap();
    void chatStore.send(companion, text);
    setDraft("");
  }

  const canSend = draft.trim().length > 0 && !typing && ready;

  return (
    <Flex direction="column" h="100dvh" position="relative" overflow="hidden">
      {/* Companion-colour glow behind the top of the chat. */}
      <Box
        position="absolute"
        top="-120px"
        left="-20%"
        right="-20%"
        h="360px"
        bg={`radial-gradient(closest-side, ${withAlpha(companion.color, 0.45)}, transparent)`}
        animation="tlGlow 6s ease-in-out infinite"
        pointerEvents="none"
      />

      <Flex
        position="relative"
        zIndex={2}
        align="center"
        gap={3}
        px={2}
        pt="calc(8px + env(safe-area-inset-top))"
        pb={2}
        bg="rgba(14,10,10,0.55)"
        backdropFilter="saturate(180%) blur(20px)"
        borderBottomWidth="1px"
        borderColor="line"
      >
        <Box
          as="button"
          aria-label="Back"
          onClick={() => {
            // Back where they came from; to Discover when the chat was opened directly.
            if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
            else navigate("/", { replace: true });
          }}
          p={2}
          borderRadius="full"
          _active={{ bg: "elevated" }}
        >
          <LuChevronLeft size={26} />
        </Box>
        <Flex
          as="button"
          align="center"
          gap={3}
          flex="1"
          minW={0}
          textAlign="left"
          onClick={() => setShowProfile(true)}
        >
          <CompanionAvatar companion={companion} size="40px" ring />
          <Box minW={0}>
            <Text fontWeight="semibold" lineHeight="1.2">
              {companion.name}
            </Text>
            <Flex
              align="center"
              gap={1.5}
              fontSize="xs"
              color={typing ? "accent" : "muted"}
            >
              {!typing && (
                <Box boxSize="6px" borderRadius="full" bg="#3ccf6e" />
              )}
              <Text truncate>
                {typing ? "typing…" : companion.tagline}
              </Text>
            </Flex>
          </Box>
        </Flex>
        <Box
          as="button"
          aria-label={`About ${companion.name}`}
          onClick={() => setShowProfile(true)}
          p={2}
          color="muted"
        >
          <LuInfo size={22} />
        </Box>
      </Flex>

      <Box
        flex="1"
        overflowY="auto"
        position="relative"
        zIndex={1}
        px={3}
        pt={5}
        pb={3}
      >
        <IntroCard companion={companion} />
        {thread.map((m, i) => (
          <React.Fragment key={m.id}>
            <Bubble
              message={m}
              companion={companion}
              first={thread[i - 1]?.from !== m.from}
              last={
                thread[i + 1]?.from !== m.from &&
                !(typing && m.from === "companion" && i === thread.length - 1)
              }
            />
            {m.from === "user" && <BubbleNote message={m} onRetry={retry} />}
          </React.Fragment>
        ))}
        {openError && (
          <Flex direction="column" align="center" gap={2} mt={6}>
            <Text fontSize="sm" color="muted">
              Couldn't open the chat.
            </Text>
            <chakra.button
              type="button"
              fontSize="sm"
              color="accent"
              onClick={open}
            >
              Try again
            </chakra.button>
          </Flex>
        )}
        {chatStore.isOpening(companion.id) && thread.length === 0 && (
          <Flex justify="center" mt={6}>
            <Loading />
          </Flex>
        )}
        {typing && <TypingBubble companion={companion} />}
        <div ref={endRef} />
      </Box>

      <Box
        position="relative"
        zIndex={2}
        bg="rgba(14,10,10,0.85)"
        backdropFilter="blur(20px)"
        borderTopWidth="1px"
        borderColor="line"
        pb="calc(8px + env(safe-area-inset-bottom))"
      >
        {draft.length === 0 && !typing && (
          <Flex gap={2} overflowX="auto" px={3} pt={3}>
            {QUICK_REPLIES.map((q) => (
              <Box
                key={q}
                as="button"
                onClick={() => send(q)}
                flexShrink={0}
                px={3.5}
                h="34px"
                borderRadius="full"
                fontSize="sm"
                borderWidth="1px"
                borderColor={withAlpha(companion.color, 0.5)}
                bg={withAlpha(companion.color, 0.12)}
                color="rgba(255,255,255,0.9)"
                _active={{ transform: "scale(0.95)" }}
                transition="transform 0.15s"
                animation="tlFade 0.25s ease both"
              >
                {q}
              </Box>
            ))}
          </Flex>
        )}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send(draft);
          }}
        >
          <Flex align="flex-end" gap={2} px={3} pt={3}>
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.nativeEvent.isComposing
                ) {
                  e.preventDefault();
                  send(draft);
                }
              }}
              placeholder={`Message ${companion.name}`}
              aria-label="Message"
              maxLength={1000}
              autoresize
              maxH="120px"
              rows={1}
              resize="none"
              borderRadius="22px"
              bg="elevated"
              border="none"
              px={4}
              py="11px"
              minH="44px"
              fontSize="16px"
              _focusVisible={{
                outline: "none",
                boxShadow: `0 0 0 1px ${withAlpha(companion.color, 0.8)}`,
              }}
            />
            <chakra.button
              type="submit"
              disabled={!canSend}
              aria-label="Send"
              flexShrink={0}
              boxSize="44px"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="white"
              background={canSend ? ACCENT_GRADIENT : "elevated"}
              opacity={canSend ? 1 : 0.6}
              transform={canSend ? "scale(1)" : "scale(0.9)"}
              transition="all 0.2s"
              _active={{ transform: "scale(0.88)" }}
            >
              <LuArrowUp size={22} strokeWidth={2.6} />
            </chakra.button>
          </Flex>
        </form>
        <Text fontSize="10px" color="faint" textAlign="center" mt={2} px={4}>
          {companion.name} is an AI, not a real person or a therapist. In a
          crisis, call or text 988.
        </Text>
      </Box>

      <CompanionSheet
        companion={showProfile ? companion : null}
        onClose={() => setShowProfile(false)}
        onStart={() => setShowProfile(false)}
      />
    </Flex>
  );
});

export const ChatPage = observer(() => {
  const chatStore = useChatStore();
  const { companionID } = useParams();
  const companion = chatStore.findCompanion(companionID);
  if (!companion) {
    if (chatStore.catalogueState === "loading") {
      return (
        <Flex h="100dvh" align="center" justify="center">
          <Loading />
        </Flex>
      );
    }
    return <Navigate to="/" replace />;
  }
  // Keyed so switching companions starts with a fresh draft.
  return <Chat key={companion.id} companion={companion} />;
});
